import { createClient } from '@supabase/supabase-js'
import type { TechCategory } from '../constants/categories'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || ''
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || ''

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

export type Database = {
  public: {
    Tables: {
      users: {
        Row: {
          id: string
          email: string
          password: string
          created_at: string
        }
        Insert: {
          email: string
          password: string
        }
        Update: {
          email?: string
          password?: string
        }
      }
      events: {
        Row: {
          id: string
          parent_event_id: string | null
          title: string
          short_description: string | null
          description: string | null
          website_link: string | null
          image_url: string | null
          start_date: string
          start_time: string
          end_date: string | null
          end_time: string | null
          time_zone: string | null
          location: string | null
          location_detail: string | null
          category: TechCategory | null
          type: 'Conference' | 'Workshop' | 'Networking' | 'Panel' | 'Meetup' | 'Webinar' | null
          ecosystem: string | null
          modality: 'in-person' | 'online' | 'hybrid' | null
          is_public: boolean | null
          requires_approval: boolean | null
          limited_places: number | null
          created_by: string
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          parent_event_id?: string | null
          title: string
          short_description?: string | null
          description?: string | null
          website_link?: string | null
          image_url?: string | null
          start_date: string
          start_time: string
          end_date?: string | null
          end_time?: string | null
          time_zone?: string | null
          location?: string | null
          location_detail?: string | null
          category?: TechCategory | null
          type?: 'Conference' | 'Workshop' | 'Networking' | 'Panel' | 'Meetup' | 'Webinar' | null
          ecosystem?: string | null
          modality?: 'in-person' | 'online' | 'hybrid' | null
          is_public?: boolean | null
          requires_approval?: boolean | null
          limited_places?: number | null
          created_by: string
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          parent_event_id?: string | null
          title?: string
          short_description?: string | null
          description?: string | null
          website_link?: string | null
          image_url?: string | null
          start_date?: string
          start_time?: string
          end_date?: string | null
          end_time?: string | null
          time_zone?: string | null
          location?: string | null
          location_detail?: string | null
          category?: TechCategory | null
          type?: 'Conference' | 'Workshop' | 'Networking' | 'Panel' | 'Meetup' | 'Webinar' | null
          ecosystem?: string | null
          modality?: 'in-person' | 'online' | 'hybrid' | null
          is_public?: boolean | null
          requires_approval?: boolean | null
          limited_places?: number | null
          created_by?: string
          created_at?: string
          updated_at?: string
        }
      }
      event_hosts: {
        Row: {
          id: string
          event_id: string
          user_id: string | null
          email: string | null
          name: string
          avatar_url: string | null
          website: string | null
          is_main_host: boolean | null
          status: 'pending' | 'accepted' | 'declined' | null
          invited_at: string
          responded_at: string | null
          invited_by: string | null
        }
        Insert: {
          id?: string
          event_id: string
          user_id?: string | null
          email?: string | null
          name: string
          avatar_url?: string | null
          website?: string | null
          is_main_host?: boolean | null
          status?: 'pending' | 'accepted' | 'declined' | null
          invited_at?: string
          responded_at?: string | null
          invited_by?: string | null
        }
        Update: {
          id?: string
          event_id?: string
          user_id?: string | null
          email?: string | null
          name?: string
          avatar_url?: string | null
          website?: string | null
          is_main_host?: boolean | null
          status?: 'pending' | 'accepted' | 'declined' | null
          invited_at?: string
          responded_at?: string | null
          invited_by?: string | null
        }
      }
      event_helpers: {
        Row: {
          id: string
          event_id: string
          user_id: string | null
          email: string
          name: string | null
          avatar_url: string | null
          attendance_status: 'registered' | 'confirmed' | 'attended' | 'no_show' | 'cancelled' | null
          rsvp_status: 'going' | 'maybe' | 'not_going' | null
          approval_status: 'pending' | 'approved' | 'rejected' | null
          approved_by: string | null
          approved_at: string | null
          registered_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          event_id: string
          user_id?: string | null
          email: string
          name?: string | null
          avatar_url?: string | null
          attendance_status?: 'registered' | 'confirmed' | 'attended' | 'no_show' | 'cancelled' | null
          rsvp_status?: 'going' | 'maybe' | 'not_going' | null
          approval_status?: 'pending' | 'approved' | 'rejected' | null
          approved_by?: string | null
          approved_at?: string | null
          registered_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          event_id?: string
          user_id?: string | null
          email?: string
          name?: string | null
          avatar_url?: string | null
          attendance_status?: 'registered' | 'confirmed' | 'attended' | 'no_show' | 'cancelled' | null
          rsvp_status?: 'going' | 'maybe' | 'not_going' | null
          approval_status?: 'pending' | 'approved' | 'rejected' | null
          approved_by?: string | null
          approved_at?: string | null
          registered_at?: string
          updated_at?: string
        }
      }
      event_users: {
        Row: {
          id: string
          event_id: string
          user_id: string | null
          email: string
          name: string | null
          avatar_url: string | null
          user_role: 'invitee' | 'vip' | 'speaker' | 'sponsor' | 'organizer' | 'staff' | null
          invitation_status: 'pending' | 'accepted' | 'declined' | 'expired' | null
          can_invite_others: boolean | null
          can_see_attendee_list: boolean | null
          invited_by: string | null
          invited_at: string
          responded_at: string | null
        }
        Insert: {
          id?: string
          event_id: string
          user_id?: string | null
          email: string
          name?: string | null
          avatar_url?: string | null
          user_role?: 'invitee' | 'vip' | 'speaker' | 'sponsor' | 'organizer' | 'staff' | null
          invitation_status?: 'pending' | 'accepted' | 'declined' | 'expired' | null
          can_invite_others?: boolean | null
          can_see_attendee_list?: boolean | null
          invited_by?: string | null
          invited_at?: string
          responded_at?: string | null
        }
        Update: {
          id?: string
          event_id?: string
          user_id?: string | null
          email?: string
          name?: string | null
          avatar_url?: string | null
          user_role?: 'invitee' | 'vip' | 'speaker' | 'sponsor' | 'organizer' | 'staff' | null
          invitation_status?: 'pending' | 'accepted' | 'declined' | 'expired' | null
          can_invite_others?: boolean | null
          can_see_attendee_list?: boolean | null
          invited_by?: string | null
          invited_at?: string
          responded_at?: string | null
        }
      }
    }
  }
}